import {
  GET_TRAINING_DETOURS,
  REMOVE_DETOUR,
  tokenOptions,
} from "../../backend/graphql";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import { isReadonly } from "../../app/features/auth/authTypes";
import { Box } from "@mui/material";
import { backendURL } from "../../constants";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { useMutation, useQuery } from "@apollo/client";
import ActionIconButton from "../widgets/ActionIconButton";
import BuildIcon from "@mui/icons-material/Build";
import PaginatedGallery from "../widgets/PaginatedGallery";
import ThumbnailCard from "../widgets/ThumbnailCard";
import SectionPaper from "../widgets/SectionPaper";
import PageTitle from "../widgets/PageTitle";
import DeleteIconButton from "../widgets/DeleteIconButton";
import { galleryLayout, GALLERY_CHROME } from "../widgets/galleryLayout";

type LearningDetoursGridProps = {
  computedWidth: number;
  computedHeight: number;
};

function LearningDetoursGrid({
  computedWidth,
  computedHeight,
}: LearningDetoursGridProps) {
  const token = useAppSelector((state) => state.auth.token);
  const readonly = isReadonly(useAppSelector((state) => state.auth.role));
  const [currentPage, setCurrentPage] = useHistoryState("currentPage", 1);
  const { cols, rows, perPage } = galleryLayout(
    computedWidth,
    215,
    245,
    0,
    computedHeight,
  );
  const [removeDetour] = useMutation(REMOVE_DETOUR, tokenOptions(token));

  const { loading, error, data, refetch } = useQuery(GET_TRAINING_DETOURS, {
    ...tokenOptions(token),
    variables: { take: perPage, skip: (currentPage - 1) * perPage, filter: "" },
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  const removeButton = (imageID: string) => {
    removeDetour({ variables: { id: imageID } }).then(() => refetch());
  };

  const items = data.trainingDetours.items;

  return (
    <PaginatedGallery
      width={computedWidth}
      height={computedHeight}
      cols={cols}
      rows={rows}
      page={currentPage}
      onPage={setCurrentPage}
      hasNext={items.length >= perPage}
    >
      {items.map((image: any) => (
        <ThumbnailCard
          key={image.id}
          size={198}
          src={`${backendURL}/training/detour/${image.id}/clip`}
          hoverSrc={`${backendURL}/training/detour/${image.id}/golden`}
          alt={`Detour ${image.id}`}
          // A read-only user looks at the samples; it neither edits nor removes them.
          actions={
            readonly ? undefined : (
              <>
                <ActionIconButton
                  onClick={() =>
                    window.open(`/learning/detour/edit/${image.id}`, "_blank")
                  }
                >
                  <BuildIcon fontSize="small" />
                </ActionIconButton>
                <DeleteIconButton onClick={() => removeButton(image.id)} />
              </>
            )
          }
        />
      ))}
    </PaginatedGallery>
  );
}

const LearningDetours = () => {
  return (
    <SectionPaper
      sx={{
        height: `calc(100vh - ${GALLERY_CHROME}px)`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <PageTitle />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <AutoSizer>
          {(size: Size) => (
            <LearningDetoursGrid
              computedWidth={size.width}
              computedHeight={size.height}
            />
          )}
        </AutoSizer>
      </Box>
    </SectionPaper>
  );
};

export default LearningDetours;
